// services/notificationService.js
import { db, auth } from "../firebase/config";

class NotificationService {
  constructor() {
    this.listeners = new Map();
    this.notifications = new Map();
    this.unsubscribe = null;
  }

  async startListening(userId, callback) {
    if (!userId) return;

    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const { collection, query, where, orderBy, onSnapshot } = await import("firebase/firestore");
    const notificationsRef = collection(db, "notifications");
    const q = query(
      notificationsRef,
      where("userId", "==", userId),
      where("status", "!=", "deleted"),
      orderBy("status", "asc"),
      orderBy("createdAt", "desc")
    );

    this.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const notificationObj = {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            statusUpdatedAt: data.statusUpdatedAt?.toDate() || null,
          };
          notifications.push(notificationObj);
        });

        this.notifications.clear();
        notifications.forEach((notif) => {
          this.notifications.set(notif.id, notif);
        });

        this.listeners.forEach((listener) => {
          listener(notifications);
        });

        if (callback) {
          callback(notifications);
        }
      },
      (error) => {
        console.error("Error in notification listener:", error);
      }
    );
  }

  stopListening() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.notifications.clear();
    this.listeners.clear();
  }

  subscribe(id, callback) {
    if (!id || typeof callback !== "function") {
      return () => {};
    }
    this.listeners.set(id, callback);
    if (this.notifications.size > 0) {
      const notifArray = Array.from(this.notifications.values());
      callback(notifArray);
    }
    return () => {
      this.listeners.delete(id);
    };
  }

  async createNotification(userId, notificationData) {
    try {
      if (!userId) throw new Error("userId is required");

      const { collection, doc, setDoc, serverTimestamp, Timestamp } = await import("firebase/firestore");
      const notificationRef = doc(collection(db, "notifications"));

      const validTypes = ["success", "error", "warning", "info", "system"];
      const type = validTypes.includes(notificationData.type) ? notificationData.type : "info";

      const notification = {
        id: notificationRef.id,
        userId,
        title: notificationData.title || "Notification",
        message: notificationData.message || "",
        type,
        link: notificationData.link || null,
        linkText: notificationData.linkText || null,
        status: "active",
        statusUpdatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
      };

      if (notificationData.userName) notification.userName = notificationData.userName;
      if (notificationData.userAvatar) notification.userAvatar = notificationData.userAvatar;
      if (notificationData.action) notification.action = notificationData.action;
      if (notificationData.meta) notification.meta = notificationData.meta;

      await setDoc(notificationRef, notification);
      return notification;
    } catch (error) {
      console.error("Error creating notification:", error);
      throw error;
    }
  }

  async createAdminNotification(data) {
    try {
      const { collection, addDoc, doc, getDoc, getDocs, query, where, serverTimestamp } = await import("firebase/firestore");
      const appealRef = await addDoc(collection(db, "banAppeals"), {
        ...data,
        status: "pending",
        createdAt: serverTimestamp(),
        reviewedBy: null,
        reviewedAt: null,
        decision: null,
      });

      const currentUser = auth.currentUser;
      try {
        if (currentUser) {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          const userData = userDoc.data();

          if (userData?.permissions?.isAdmin) {
            const usersRef = collection(db, "users");
            const adminsQuery = query(usersRef, where("permissions.isAdmin", "==", true));
            const adminsSnapshot = await getDocs(adminsQuery);
            const adminUsers = [];

            adminsSnapshot.forEach((doc) => {
              adminUsers.push({ id: doc.id, ...doc.data() });
            });

            if (adminUsers.length > 0) {
              const notificationPromises = adminUsers.map((admin) => {
                return this.createNotification(admin.id, {
                  title: `Ban Appeal Request${data.userId ? ` from User ${data.userId}` : ""}`,
                  message: data.message.substring(0, 100) + (data.message.length > 100 ? "..." : ""),
                  type: "system",
                  link: `/admin/appeals/${appealRef.id}`,
                  meta: { appealId: appealRef.id },
                });
              });
              await Promise.all(notificationPromises);
            }
          }
        }
      } catch (error) {
        console.error("Error notifying admins about appeal:", error);
      }

      return { id: appealRef.id, ...data };
    } catch (error) {
      console.error("Error creating admin notification:", error);
      throw error;
    }
  }

  async createStatusNotification(userId, { title, message, type = "info", link = null, linkText = null }) {
    if (!userId) return null;
    return this.createNotification(userId, { title, message, type, link, linkText });
  }

  async createSocialNotification(userId, { userName, userAvatar, action, message, link = null }) {
    if (!userId) return null;
    return this.createNotification(userId, {
      title: userName,
      message,
      type: "info",
      link,
      userName,
      userAvatar,
      action,
    });
  }

  async markAsRead(notificationId) {
    try {
      if (!notificationId) return;
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationRef = doc(db, "notifications", notificationId);
      const notification = this.notifications.get(notificationId);

      if (notification && ["active", "new"].includes(notification.status)) {
        await updateDoc(notificationRef, {
          status: "read",
          statusUpdatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      throw error;
    }
  }

  async markAllAsRead(userId) {
    try {
      if (!userId) return;
      const { collection, query, where, getDocs, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationsRef = collection(db, "notifications");
      const q = query(notificationsRef, where("userId", "==", userId), where("status", "==", "new"));
      const snapshot = await getDocs(q);

      const updatePromises = snapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          status: "read",
          statusUpdatedAt: serverTimestamp(),
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  }

  async closeNotification(notificationId, options = {}) {
    try {
      if (!notificationId) return;
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationRef = doc(db, "notifications", notificationId);
      const newStatus = options.markAsRead ? "read" : "new";
      await updateDoc(notificationRef, {
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error closing notification:", error);
      throw error;
    }
  }

  async reopenNotification(notificationId) {
    try {
      if (!notificationId) return;
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, {
        status: "active",
        statusUpdatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error reopening notification:", error);
      throw error;
    }
  }

  async dismissNotification(notificationId) {
    try {
      if (!notificationId) return;
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, {
        status: "dismissed",
        statusUpdatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error dismissing notification:", error);
      throw error;
    }
  }

  async cleanupExpiredNotifications(userId) {
    try {
      if (!userId) return;
      const { collection, query, where, getDocs, updateDoc, serverTimestamp, Timestamp } = await import("firebase/firestore");
      const notificationsRef = collection(db, "notifications");
      const q = query(
        notificationsRef,
        where("userId", "==", userId),
        where("expiresAt", "<=", Timestamp.now()),
        where("status", "!=", "deleted")
      );
      const snapshot = await getDocs(q);

      const deletePromises = snapshot.docs.map((doc) =>
        updateDoc(doc.ref, {
          status: "deleted",
          statusUpdatedAt: serverTimestamp(),
          deletionReason: "expired",
        })
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error cleaning up expired notifications:", error);
    }
  }

  async cleanupOldNotifications(userId, maxAgeDays = 30, maxCount = 30) {
    try {
      if (!userId) return;
      const { collection, query, where, orderBy, getDocs, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const notificationsRef = collection(db, "notifications");
      const q = query(
        notificationsRef,
        where("userId", "==", userId),
        where("status", "!=", "deleted"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ref: doc.ref,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));

      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - maxAgeDays);
      const toDelete = [];
      const oldNotifications = notifications.filter((n) => n.createdAt < cutoffDate);
      toDelete.push(...oldNotifications);

      const remainingNotifications = notifications.filter((n) => !toDelete.includes(n));
      if (remainingNotifications.length > maxCount) {
        remainingNotifications.sort((a, b) => a.createdAt - b.createdAt);
        toDelete.push(...remainingNotifications.slice(0, remainingNotifications.length - maxCount));
      }

      const deletePromises = toDelete.map((notification) =>
        updateDoc(notification.ref, {
          status: "deleted",
          statusUpdatedAt: serverTimestamp(),
          deletionReason: "automatic-cleanup",
        })
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error cleaning up old notifications:", error);
    }
  }

  getActiveCount() {
    let count = 0;
    this.notifications.forEach((notif) => {
      if (notif.status === "active") count++;
    });
    return count;
  }

  getUnreadCount() {
    let count = 0;
    this.notifications.forEach((notif) => {
      if (notif.status === "new") count++;
    });
    return count;
  }
}

const notificationService = new NotificationService();
export default notificationService;
